import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import { Copy, Check, KeyRound, AlertTriangle } from 'lucide-react';
import { copyFromVisibleNode } from '../../utils/clipboard';

/**
 * One-time API-key reveal dialog (spec §1: "API keys are shown exactly once").
 *
 * Used after creating a user or resetting a key. The key is never fetched again,
 * so we show it in a copyable box and warn that it won't be shown again. Themed by
 * whatever MUI ThemeProvider wraps it (the console supplies one).
 *
 * Copying goes through `copyFromVisibleNode`, which copies the rendered key
 * node rather than a detached textarea — the dialog's focus trap would
 * otherwise leave the copy empty when running inside the iframe.
 */
const ApiKeyRevealDialog = ({
  open,
  apiKey,
  title = 'API key created',
  subtitle = 'Copy this key now — it will not be shown again.',
  roles = null,
  // When the value is genuinely unrecoverable, a stray backdrop click or Esc
  // must not be able to lose it — the customer has to dismiss deliberately.
  requireExplicitDismiss = false,
  // Extra content rendered under the key box (e.g. install snippets with the
  // real value already substituted).
  children = null,
  onClose,
}) => {
  const keyRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const handleCopy = async () => {
    const ok = await copyFromVisibleNode(apiKey, keyRef.current);
    if (ok) {
      setCopyFailed(false);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      // The key is left selected so the user can copy it manually.
      setCopyFailed(true);
      setTimeout(() => setCopyFailed(false), 6000);
    }
  };

  const handleClose = () => {
    setCopied(false);
    setCopyFailed(false);
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={requireExplicitDismiss ? undefined : handleClose}
      disableEscapeKeyDown={requireExplicitDismiss}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 700 }}>
        <KeyRound size={22} />
        {title}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            bgcolor: 'rgba(245, 158, 11, 0.12)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
          }}
        >
          <AlertTriangle size={18} color="#f59e0b" style={{ flexShrink: 0, marginTop: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {/* Always-present warning (spec §1: keys are shown exactly once). */}
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              This key won't be shown again — copy it now.
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'action.hover',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            ref={keyRef}
            component="code"
            sx={{
              flex: 1,
              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              fontSize: 13,
              wordBreak: 'break-all',
              userSelect: 'all',
            }}
          >
            {apiKey}
          </Typography>
          <IconButton onClick={handleCopy} size="small" title="Copy to clipboard">
            {copied ? <Check size={18} color="#10b981" /> : <Copy size={18} />}
          </IconButton>
        </Box>

        {copyFailed && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'warning.main' }}>
            Couldn't access the clipboard here — the key is selected, press Ctrl/⌘ + C to copy it.
          </Typography>
        )}

        {children && <Box sx={{ mt: 2.5 }}>{children}</Box>}

        {Array.isArray(roles) && roles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
              ASSIGNED ROLES
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
              {roles.map((r) => (
                <Chip key={r} label={r} size="small" />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCopy} startIcon={copied ? <Check size={16} /> : <Copy size={16} />}>
          {copied ? 'Copied' : 'Copy key'}
        </Button>
        <Button onClick={handleClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApiKeyRevealDialog;
